import { useEffect, useMemo, memo, useRef } from 'react';
import {
  getDaysInMonth,
  getDate,
  isSameDay,
  addDays,
  subDays,
  isToday,
  isSameMonth,
  isBefore,
  isAfter,
} from 'date-fns';
import { getShortWeeks, cloneDate } from 'utils';
import clsx from 'clsx';
import { useStyles } from './style';

const DAYS_IN_WEEK = 7;
const weekShort = getShortWeeks();

function getDays(date: Date): Array<Date> {
  let daysInMonth = getDaysInMonth(date);
  let days: Array<Date> = [];

  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(date.getFullYear(), date.getMonth(), i));
  }
  return days;
}

type OptionsType = {
  firstDayOfWeek?: number;
  forceSixRows?: boolean;
};

function getWeeks(
  date: Date,
  { firstDayOfWeek = 1, forceSixRows = false }: OptionsType
) {
  let days = getDays(date),
    daysInMonth = days.length,
    week: Date[] = [],
    weeks: Array<Array<Date>> = [];

  // build weeks array
  days.forEach((day) => {
    if (week.length > 0 && day.getDay() === firstDayOfWeek) {
      weeks.push(week);
      week = [];
    }
    week.push(day);
    if (days.indexOf(day) === days.length - 1) {
      weeks.push(week);
    }
  });

  const firstWeek = weeks[0];
  for (let i = DAYS_IN_WEEK - firstWeek.length; i > 0; i--) {
    const outsideDate = cloneDate(firstWeek[0]);
    outsideDate.setDate(firstWeek[0].getDate() - 1);
    firstWeek.unshift(outsideDate);
    daysInMonth++;
  }

  const lastWeek = weeks[weeks.length - 1];
  for (let i = lastWeek.length; i < DAYS_IN_WEEK; i++) {
    const outsideDate = cloneDate(lastWeek[lastWeek.length - 1]);
    outsideDate.setDate(lastWeek[lastWeek.length - 1].getDate() + 1);
    lastWeek.push(outsideDate);
    daysInMonth++;
  }

  if (forceSixRows && daysInMonth < 42) {
    let lastDayOfMonth = weeks[weeks.length - 1][6];
    let lastWeek: Date[] = [];
    let i = 1;
    while (daysInMonth < 42) {
      let lastDayOfMonthClone = cloneDate(lastDayOfMonth);
      let day = new Date(
        lastDayOfMonthClone.setDate(lastDayOfMonthClone.getDate() + i)
      );
      if (lastWeek.length > 0 && day.getDay() === firstDayOfWeek) {
        weeks.push(lastWeek);
        lastWeek = [];
      }
      lastWeek.push(day);
      daysInMonth++;
      i++;
    }
    weeks.push(lastWeek);
  }
  return weeks;
}

export type CalendarClassNamesType = {
  root?: string;
  weekDaysWrapper?: string;
  weekDaysBlock?: string;
  weekDay?: string;
  daysWrapper?: string;
  weekRow?: string;
  day?: string;
  today?: string;
  activeDay?: string;
  diffMonth?: string;
  disabledDay?: string;
};

interface Props {
  activeDate?: Date;
  currentDate: Date;
  handleChangeDate: (newDate: Date) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  cls?: CalendarClassNamesType;
}

const Calendar = memo<Props>(
  ({ currentDate, handleChangeDate, activeDate, minDate, maxDate, cls }) => {
    const classes = useStyles();
    const weeks = useMemo(() => getWeeks(currentDate, {}), [currentDate]);
    const calendarRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      calendarRef.current?.focus();
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      // e.preventDefault();
      if (!activeDate) {
        return;
      }

      let newDate = cloneDate(activeDate);

      if (e.key)
        if (e.key === 'ArrowRight') {
          const d = addDays(newDate, 1);
          if (maxDate && isAfter(d, maxDate)) {
            return;
          }
          handleChangeDate(d);
        }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const d = addDays(newDate, 7);
        if (maxDate && isAfter(d, maxDate)) {
          return;
        }
        handleChangeDate(d);
      }

      if (e.key === 'ArrowUp') {
        const d = subDays(newDate, 7);
        if (minDate && isBefore(d, minDate)) {
          return;
        }
        handleChangeDate(d);
      }

      if (e.key === 'ArrowLeft') {
        const d = subDays(newDate, 1);
        if (minDate && isBefore(d, minDate)) {
          return;
        }
        handleChangeDate(d);
      }
    };

    return (
      <div
        className={clsx(classes.root, cls?.root)}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        ref={calendarRef}
      >
        <div className={clsx(classes.weeksWrapper, cls?.weekDaysWrapper)}>
          {weekShort.map((item) => (
            <div key={item} className={clsx(classes.block, cls?.weekDaysBlock)}>
              <span className={clsx(classes.weekDay, cls?.weekDay)}>
                {item}
              </span>
            </div>
          ))}
        </div>
        <div className={cls?.daysWrapper}>
          {weeks.map((week, index) => {
            return (
              <div key={index} className={clsx(classes.week, cls?.weekRow)}>
                {week.map((day) => {
                  const active = activeDate
                    ? isSameDay(day, activeDate)
                    : false;
                  const today = isToday(day);
                  const sameMonth = currentDate
                    ? isSameMonth(day, currentDate)
                    : false;
                  const afterMaxDate = maxDate ? isAfter(day, maxDate) : false;
                  const beforeMinDate = minDate
                    ? isBefore(day, minDate)
                    : false;
                  const disabled = afterMaxDate || beforeMinDate;

                  return (
                    <div
                      className={clsx(
                        classes.block,
                        classes.dayBlock,
                        cls?.day,
                        {
                          [clsx(classes.today, cls?.today)]: today,
                          [clsx(classes.activeBlock, cls?.activeDay)]: active,
                          [clsx(classes.diffMonth, cls?.diffMonth)]: !sameMonth,
                          [clsx(classes.disabledBlock, cls?.disabledDay)]:
                            disabled,
                        }
                      )}
                      onClick={
                        active || !sameMonth || disabled
                          ? undefined
                          : () => handleChangeDate(day)
                      }
                      key={day.toDateString()}
                    >
                      {sameMonth ? getDate(day) : null}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

export default Calendar;
